import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Camera, ArrowLeft, MapPin, Clock, Wrench, MessageCircle, ListChecks, ExternalLink,
} from 'lucide-react';
import photoApi from '@/lib/photoApi';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import EventChat from '@/components/EventChat';

function SpotCard({ spot: s, t }) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="font-semibold text-slate-900">{s.name}</p>
          {s.address && <p className="text-sm text-slate-600 mt-0.5">{s.address}</p>}
          <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-700">
            {s.my_arrive_time && (
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> {t('photographer.arriveAt', { time: s.my_arrive_time })}
              </span>
            )}
            {s.my_leave_time && (
              <span className="inline-flex items-center gap-1 text-slate-500">
                · {t('photographer.leaveAt', { time: s.my_leave_time })}
              </span>
            )}
          </div>
          {(s.modalities || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {s.modalities.map((m) => (
                <span key={m} className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{m}</span>
              ))}
            </div>
          )}
          {s.notes && <p className="text-xs text-slate-500 mt-2 italic">{s.notes}</p>}
        </div>
        {s.lat && s.lng && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${s.lat},${s.lng}`}
            target="_blank" rel="noreferrer"
            className="text-emerald-600 hover:text-emerald-700 text-xs inline-flex items-center gap-1 ml-3"
          >
            <MapPin className="w-3.5 h-3.5" /> {t('photographer.openInMaps')}
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </Card>
  );
}


export default function PhotographerEventView() {
  const { t, i18n } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    photoApi.get(`/photographer/events/${eventId}/plan`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.data?.detail || 'error'));
  }, [eventId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="p-6 text-center">
          <p className="text-slate-700 mb-3">{error === 'You are not assigned to this event' ? 'You are not assigned to this event.' : t('auth.errors.generic')}</p>
          <Button onClick={() => navigate('/photographer/dashboard')}>{t('common.back')}</Button>
        </Card>
      </div>
    );
  }
  if (!data) return <div className="p-10 text-center text-slate-500">{t('common.loading')}</div>;

  const { event, spots, photographer, spots_by_day: spotsByDay } = data;
  const me = photographer.name;
  const isMultiDay = (event.days?.length || 1) > 1;
  const formatDayHeader = (d) => {
    if (!d?.date) return '';
    try {
      return new Date(d.date).toLocaleDateString(i18n.language, {
        weekday: 'long', day: 'numeric', month: 'long',
      });
    } catch { return d.date; }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to="/photographer/dashboard" className="text-slate-500 hover:text-slate-700">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Camera className="w-5 h-5 text-emerald-600" />
          <span className="font-bold text-slate-900">{event.name}</span>
        </div>
        <LanguageSwitcher />
      </header>

      <main className="max-w-4xl mx-auto p-4 md:p-6">
        <Card className="p-5 mb-4">
          <p className="text-sm text-slate-500">
            {new Date(event.date).toLocaleDateString(i18n.language, {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
            })} · {event.start_time}–{event.end_time}
          </p>
          {event.location && <p className="text-sm text-slate-700 mt-1">{event.location}</p>}
          {event.description && <p className="text-sm text-slate-600 mt-2">{event.description}</p>}
        </Card>

        <Tabs defaultValue="plan">
          <TabsList className="mb-4">
            <TabsTrigger value="plan"><ListChecks className="w-4 h-4 mr-1" /> {t('photographer.planTabPlan')}</TabsTrigger>
            <TabsTrigger value="chat"><MessageCircle className="w-4 h-4 mr-1" /> {t('photographer.planTabChat')}</TabsTrigger>
            <TabsTrigger value="gear"><Wrench className="w-4 h-4 mr-1" /> {t('photographer.planTabGear')}</TabsTrigger>
          </TabsList>

          <TabsContent value="plan">
            <h2 className="font-semibold text-slate-700 mb-3">{t('photographer.mySchedule')}</h2>
            {spots.length === 0 ? (
              <Card className="p-6 text-center text-slate-500">{t('photographer.noSpots')}</Card>
            ) : isMultiDay && Array.isArray(spotsByDay) && spotsByDay.length > 0 ? (
              <div className="space-y-6">
                {spotsByDay.map((group) => (
                  <div key={group.day.id}>
                    <div className="flex items-baseline justify-between mb-2 pb-1 border-b border-slate-200">
                      <h3 className="text-sm font-semibold text-slate-800">
                        {group.day.label || formatDayHeader(group.day)}
                      </h3>
                      <span className="text-xs text-slate-500">
                        {group.day.start_time}–{group.day.end_time}
                      </span>
                    </div>
                    {group.spots.length === 0 ? (
                      <Card className="p-4 text-center text-slate-400 text-sm">{t('photographer.noSpots')}</Card>
                    ) : (
                      <div className="space-y-3">
                        {group.spots.map((s) => <SpotCard key={s.id} spot={s} t={t} />)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {spots.map((s) => <SpotCard key={s.id} spot={s} t={t} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="chat">
            <EventChat eventId={eventId} apiClient={photoApi} selfName={me} />
          </TabsContent>

          <TabsContent value="gear">
            <h2 className="font-semibold text-slate-700 mb-3">{t('photographer.myGear')}</h2>
            {(photographer.personal_gear || []).length === 0 ? (
              <Card className="p-6 text-center text-slate-500">{t('photographer.noGear')}</Card>
            ) : (
              <Card className="overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    {photographer.personal_gear.map((g, i) => (
                      <tr key={i} className="border-t first:border-t-0">
                        <td className="p-3 font-medium text-slate-800">{g.name}</td>
                        <td className="p-3 text-slate-500 text-xs uppercase">{g.type}</td>
                        <td className="p-3 text-slate-600">{g.details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
